import React, { useState } from 'react';
import Slider from 'react-slick';
import axios from 'axios';
import '../styles/ContactUs.css';
import '../styles/pages/StateSelection.css';
import '../styles/GlobalSections.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const ContactUs = () => {
  const [formData, setFormData] = useState({
    requestType: '',
    fullName: '',
    email: '',
    designation: '',
    mobile: '',
    organizationName: '',
    city: '',
    taluka: '',
    district: '',
    state: '',
    message: '',
    referralAgentCode: '',
    referralAgentName: ''
  });

  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [timer, setTimer] = useState(0);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Request Type
    if (!formData.requestType) {
      newErrors.requestType = 'Please select a request type';
    }

    // Full Name - Alphabets only
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (!/^[A-Za-z\s]+$/.test(formData.fullName)) {
      newErrors.fullName = 'Name should contain only alphabets';
    }

    // Email - Valid format
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    // Designation
    if (!formData.designation.trim()) {
      newErrors.designation = 'Designation is required';
    }

    // Phone - 10 digits
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be exactly 10 digits';
    }

    // Organization Name
    if (!formData.organizationName.trim()) {
      newErrors.organizationName = 'Organization name is required';
    }

    // City
    if (!formData.city.trim()) {
      newErrors.city = 'City/Village is required';
    }

    // Taluka
    if (!formData.taluka.trim()) {
      newErrors.taluka = 'Taluka is required';
    }

    // District
    if (!formData.district.trim()) {
      newErrors.district = 'District is required';
    }

    // State
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    // Message - Optional but if provided, ≥ 20 characters
    if (formData.message.trim() && formData.message.trim().length < 20) {
      newErrors.message = 'Message should be at least 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStateClick = (state) => {
    const element = document.getElementById('registration-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    sessionStorage.setItem('selectedState', state);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Start countdown timer
  const startTimer = () => {
    setTimer(60);
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Send OTP
  const handleSendOTP = async () => {
    // Validate required fields for OTP
    const otpErrors = {};
    if (!formData.fullName.trim() || !/^[A-Za-z\s]+$/.test(formData.fullName)) {
      otpErrors.fullName = 'Please enter a valid full name';
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      otpErrors.email = 'Please enter a valid email';
    }

    if (Object.keys(otpErrors).length > 0) {
      setErrors(otpErrors);
      return;
    }

    setSendingOtp(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/contact/send-otp`, {
        email: formData.email,
        fullName: formData.fullName
      });

      if (response.data.success) {
        setOtpSent(true);
        startTimer();
        alert('OTP sent to your email! Please check your inbox.');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      alert(error.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setErrors(prev => ({ ...prev, otp: 'Please enter a valid 6-digit OTP' }));
      return;
    }

    setVerifyingOtp(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/contact/verify-otp`, {
        email: formData.email,
        otp: otp
      });

      if (response.data.success) {
        setOtpVerified(true);
        alert('Email verified successfully!');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setErrors(prev => ({ 
        ...prev, 
        otp: error.response?.data?.error || 'Invalid OTP. Please try again.' 
      }));
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!otpVerified) {
      alert('Please verify your email first by entering the OTP.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/contact/submit`, formData);

      if (response.data.success) {
        setSubmitSuccess(true);
        setFormData({
          requestType: '',
          fullName: '',
          email: '',
          designation: '',
          mobile: '',
          organizationName: '',
          city: '',
          taluka: '',
          district: '',
          state: '',
          message: '',
          referralAgentCode: '',
          referralAgentName: ''
        });
        setOtp('');
        setOtpSent(false);
        setOtpVerified(false);
        
        // Scroll to top to show success message
        const element = document.getElementById('registration-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      alert(error.response?.data?.error || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-us-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-content">
          <h1 className="contact-hero-title">Get Registered – Start Your User Journey</h1>
          <p className="contact-hero-description">
            New to LCS? Register your organization and start KYC Verification within minutes. 
            Receive credentials, wallet access, and PDF agreements — ready to use.
          </p>
        </div>
      </section>

      {/* Main Registration Section */}
      <section id="registration-section" className="registration-section section-white">
        <div className="registration-container">
          
          {/* Introduction Column */}
          <div className="registration-intro">
            <div className="intro-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h2>Why Register with LCS?</h2>
            <p className="intro-text">
              Join hundreds of organizations streamlining their KYC verification process. 
              Our platform ensures fast, secure, and compliant KYC checks.
            </p>
            <div className="intro-stats">
              <div className="stat-item">
                <span className="stat-number">5 min</span>
                <span className="stat-label">Average Setup Time</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Access to Platform</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">100%</span>
                <span className="stat-label">Secure</span>
              </div>
            </div>
          </div>

          {/* Benefits Column */}
          <div className="registration-benefits">
            <h3>Key Benefits</h3>
            <ul className="benefits-list">
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Instant credentials post Admin approval</span>
              </li>
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Personalized setup after first login</span>
              </li>
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Zero downtime onboarding</span>
              </li>
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>RBI-compliant agreements included</span>
              </li>
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Secure wallet setup with transparent tracking</span>
              </li>
              <li>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>Dedicated support for smooth onboarding</span>
              </li>
            </ul>
          </div>

          {/* Contact Form Column */}
          <div className="registration-form-container">
            <h3>Request Registration</h3>
            <p className="form-subtitle">Fill out the form below and our team will get back to you shortly</p>
            
            {submitSuccess && (
              <div className="success-message">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                Registration request submitted successfully! We'll contact you soon.
              </div>
            )}

            <form onSubmit={handleSubmit} className="registration-form">
              {/* Request Type Dropdown */}
              <div className="form-group">
                <label htmlFor="requestType">
                  Request Type <span className="required">*</span>
                </label>
                <select
                  id="requestType"
                  name="requestType"
                  value={formData.requestType}
                  onChange={handleChange}
                  className={errors.requestType ? 'error' : ''}
                >
                  <option value="">Select Request Type</option>
                  <option value="New Membership">New Membership</option>
                  <option value="Business Partnership">Business Partnership</option>
                </select>
                {errors.requestType && <span className="error-message">{errors.requestType}</span>}
              </div>

              {/* Full Name */}
              <div className="form-group">
                <label htmlFor="fullName">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={errors.fullName ? 'error' : ''}
                  placeholder="Enter your full name"
                  disabled={otpVerified}
                />
                {errors.fullName && <span className="error-message">{errors.fullName}</span>}
              </div>

              {/* Email with OTP Verification */}
              <div className="form-group">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                  {otpVerified && <span className="verified-badge">✓ Verified</span>}
                </label>
                <div className="email-otp-container">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="your.email@company.com"
                    disabled={otpSent || otpVerified}
                  />
                  {!otpSent && !otpVerified && (
                    <button
                      type="button"
                      onClick={handleSendOTP}
                      className="send-otp-button"
                      disabled={sendingOtp || !formData.email || !formData.fullName}
                    >
                      {sendingOtp ? 'Sending...' : 'Send OTP'}
                    </button>
                  )}
                </div>
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              {/* OTP Input (shown after OTP is sent) */}
              {otpSent && !otpVerified && (
                <div className="form-group otp-section">
                  <label htmlFor="otp">
                    Enter OTP <span className="required">*</span>
                  </label>
                  <div className="otp-input-container">
                    <input
                      type="text"
                      id="otp"
                      value={otp}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtp(val);
                        if (errors.otp) {
                          setErrors(prev => ({ ...prev, otp: '' }));
                        }
                      }}
                      className={errors.otp ? 'error' : ''}
                      placeholder="Enter 6-digit OTP"
                      maxLength="6"
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOTP}
                      className="verify-otp-button"
                      disabled={verifyingOtp || otp.length !== 6}
                    >
                      {verifyingOtp ? 'Verifying...' : 'Verify OTP'}
                    </button>
                  </div>
                  {errors.otp && <span className="error-message">{errors.otp}</span>}
                  <div className="otp-info">
                    <small>Check your email for the 6-digit OTP code</small>
                    {timer > 0 ? (
                      <small className="timer">Resend OTP in {timer}s</small>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setOtpSent(false);
                          setOtp('');
                        }}
                        className="resend-otp-link"
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Referral Agent Code */}
              <div className="form-group">
                <label htmlFor="referralAgentCode">
                  Referral Agent Code <span className="optional">(Optional)</span>
                </label>
                <input
                  type="text"
                  id="referralAgentCode"
                  name="referralAgentCode"
                  value={formData.referralAgentCode}
                  onChange={handleChange}
                  className={errors.referralAgentCode ? 'error' : ''}
                  placeholder="Enter referral agent code if you have one"
                />
                {errors.referralAgentCode && <span className="error-message">{errors.referralAgentCode}</span>}
              </div>

              {/* Referral Agent Name */}
              <div className="form-group">
                <label htmlFor="referralAgentName">
                  Referral Agent Name <span className="optional">(Optional)</span>
                </label>
                <input
                  type="text"
                  id="referralAgentName"
                  name="referralAgentName"
                  value={formData.referralAgentName}
                  onChange={handleChange}
                  className={errors.referralAgentName ? 'error' : ''}
                  placeholder="Enter referral agent name if applicable"
                />
                {errors.referralAgentName && <span className="error-message">{errors.referralAgentName}</span>}
              </div>

              {/* Designation */}
              <div className="form-group">
                <label htmlFor="designation">
                  Designation <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  className={errors.designation ? 'error' : ''}
                  placeholder="e.g., Manager, CEO, Director"
                />
                {errors.designation && <span className="error-message">{errors.designation}</span>}
              </div>

              {/* Mobile/Telephone */}
              <div className="form-group">
                <label htmlFor="mobile">
                  Mobile/Telephone <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  className={errors.mobile ? 'error' : ''}
                  placeholder="10-digit mobile number"
                  maxLength="10"
                />
                {errors.mobile && <span className="error-message">{errors.mobile}</span>}
              </div>

              {/* Organization/Bank/Company Name */}
              <div className="form-group">
                <label htmlFor="organizationName">
                  Organization/Bank/Company Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="organizationName"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleChange}
                  className={errors.organizationName ? 'error' : ''}
                  placeholder="Enter organization name"
                />
                {errors.organizationName && <span className="error-message">{errors.organizationName}</span>}
              </div>

              {/* City/Village */}
              <div className="form-group">
                <label htmlFor="city">
                  City/Village <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={errors.city ? 'error' : ''}
                  placeholder="Enter city or village name"
                />
                {errors.city && <span className="error-message">{errors.city}</span>}
              </div>

              {/* Taluka */}
              <div className="form-group">
                <label htmlFor="taluka">
                  Taluka <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="taluka"
                  name="taluka"
                  value={formData.taluka}
                  onChange={handleChange}
                  className={errors.taluka ? 'error' : ''}
                  placeholder="Enter taluka name"
                />
                {errors.taluka && <span className="error-message">{errors.taluka}</span>}
              </div>

              {/* District */}
              <div className="form-group">
                <label htmlFor="district">
                  District <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="district"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  className={errors.district ? 'error' : ''}
                  placeholder="Enter district name"
                />
                {errors.district && <span className="error-message">{errors.district}</span>}
              </div>

              {/* State */}
              <div className="form-group">
                <label htmlFor="state">
                  State <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className={errors.state ? 'error' : ''}
                  placeholder="Enter state name"
                />
                {errors.state && <span className="error-message">{errors.state}</span>}
              </div>

              {/* Message */}
              <div className="form-group">
                <label htmlFor="message">
                  Message <span className="optional">(Optional)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={errors.message ? 'error' : ''}
                  placeholder="Tell us about your requirements (min 20 characters if provided)"
                  rows="4"
                ></textarea>
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <button 
                type="submit" 
                className="submit-button" 
                disabled={isSubmitting || !otpVerified}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Submitting...
                  </>
                ) : (
                  'Request Registration'
                )}
              </button>

              {!otpVerified && (
                <p className="verification-note">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  Please verify your email with OTP before submitting
                </p>
              )}
            </form>
          </div>

        </div>
      </section>

      {/* Connect With Us Section */}
      <section className="connect-section section-light-blue">
        <div className="connect-container">
          <h2>Connect with Us to Register</h2>
          <p className="connect-subtitle">Can't wait? Connect with us directly — our team will help you onboard faster.</p>
          
          <div className="contact-methods">
            <div className="contact-card">
              <div className="contact-icon email-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <h3>Email Us</h3>
              <p className="email-address">sales@lcsolution.in</p>
              <p className="contact-hours">Subject: "User Registration Request"</p>
              <a href="mailto:sales@lcsolution.in?subject=User%20Registration%20Request" className="contact-action-button">Send Email</a>
            </div>
          </div>
        </div>
      </section>

      {/* Head Quarter Office Section
      <section className="hq-office-section">
        <div className="hq-office-container">
          <div className="hq-office-section-heading">
            <h2>Our Office Location</h2>
          </div>
          <div className="hq-office-text">
            <h2>Head Quarter Office</h2>
            <p className="office-address-text">Near Ambedkar Statue, Above Champavati Hardware Store, H.No.3.10.7, Hiralal chowk Beed, NA, Beed, Bid-431122, Maharashtra, India</p>
            <h2>Contact No</h2>
            <p className="office-address-text"> 8642995000</p>
          </div>
          <div className="hq-office-map-wrapper">
            <iframe
              title="Head Quarter Office Location"
              src="https://www.openstreetmap.org/export/embed.html?bbox=75.7470%2C18.9810%2C75.7730%2C18.9970&layer=mapnik&marker=18.9890%2C75.7600"
              className="office-map"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Near%20Ambedkar%20Statue,%20Above%20Champavati%20Hardware%20Store,%20Hiralal%20Chowk,%20Beed,%20Maharashtra%20431122,%20India"
              target="_blank"
              rel="noopener noreferrer"
              className="office-map-link"
            >
              Open in Google Maps
            </a>
          </div>
        </div>
      </section> */}

      {/* Process Timeline */}
      <section className="process-timeline section-white">
        <div className="timeline-container">
          <h2>Your Registration Process</h2>
          <p className="timeline-subtitle">Simple, transparent, and fast — get started in 4 easy steps</p>
          
          <div className="timeline-steps">
            <div className="timeline-step">
              <div className="step-number1">1</div>
              <div className="step-content">
                <h4>Submit Request</h4>
                <p>Fill the registration form or contact us directly</p>
              </div>
            </div>

            <div className="timeline-step">
              <div className="step-number1">2</div>
              <div className="step-content">
                <h4>Admin Review</h4>
                <p>Our team reviews and approves your registration</p>
              </div>
            </div>

            <div className="timeline-step">
              <div className="step-number1">3</div>
              <div className="step-content">
                <h4>Receive Credentials</h4>
                <p>Get login details, PDF agreement, and wallet setup</p>
              </div>
            </div>

            <div className="timeline-step">
              <div className="step-number1">4</div>
              <div className="step-content">
                <h4>Start Using LCS</h4>
                <p>Login and begin running credit checks immediately</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Selection Slider */}
      <section className="state-selection-section section-light-blue">
        <div className="state-container">
          <h2>Select Your State</h2>
          <p className="state-subtitle">
            If your bank, cooperative society, multi-state cooperative society, or any other financial institution is from a particular state, 
            then click on the name of that state from the list given below.
          </p>
          <p className="state-note">
            If the name of your state is not included in this list, click on 'Any Other State' and fill in the required information and submit it.
          </p>
          
          <Slider
            dots={true}
            infinite={true}
            speed={500}
            slidesToShow={5}
            slidesToScroll={1}
            autoplay={true}
            autoplaySpeed={3000}
            responsive={[
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 4,
                  slidesToScroll: 1
                }
              },
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: 3,
                  slidesToScroll: 1
                }
              },
              {
                breakpoint: 480,
                settings: {
                  slidesToShow: 2,
                  slidesToScroll: 1
                }
              }
            ]}
            className="states-slider"
          >
            {['Karnataka', 'Delhi', 'Maharashtra', 'Gujarat', 'Uttar Pradesh', 'Andhra Pradesh', 
              'Telangana', 'Chhattisgarh', 'Rajasthan', 'Kerala', 'Punjab', 'Haryana', 
              'Madhya Pradesh', 'Goa', 'Tamil Nadu', 'Any Other State'].map((state, index) => (
              <div key={index} className="state-card-wrapper" onClick={() => handleStateClick(state)}>
                <div className="state-card">
                  <div className="state-icon">📍</div>
                  <h3>{state}</h3>
                </div>
              </div>
            ))}
          </Slider>

          <div className="state-info-box">
            <div className="info-icon">ℹ️</div>
            <p>
              Our representative staff or office officials from the concerned state will send you complete details 
              of the next process via email.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
